import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export const SUPPORT_EMAIL = 'support@valital.com';

export function getHelpLegalUrls(language?: string) {
  const isFr = language?.includes('fr');
  return {
    termsUrl: isFr
      ? 'https://www.valital.com/fr/conditions-dutilisation'
      : 'https://www.valital.com/terms-of-use',
    privacyUrl: isFr
      ? 'https://www.valital.com/fr/declaration-de-confidentialite'
      : 'https://www.valital.com/privacy-statement',
  };
}

type ContactUsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ContactUsDialog: React.FC<ContactUsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  const handleSend = () => {
    const subject = encodeURIComponent(
      String(t('sentences:help_menu.contact_email_subject'))
    );
    const body = encodeURIComponent(message.trim());
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}${
      body ? `&body=${body}` : ''
    }`;
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="contact-us-dialog-title"
    >
      <DialogTitle
        id="contact-us-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        {String(t('sentences:help_menu.contact_us'))}
        <IconButton
          aria-label={String(t('verbs.close'))}
          onClick={handleClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {String(t('sentences:help_menu.contact_hint_prefix'))}{' '}
          <Link
            href={`mailto:${SUPPORT_EMAIL}`}
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {SUPPORT_EMAIL}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={6}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={String(t('sentences:help_menu.contact_placeholder'))}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={!message.trim()}
          sx={{ textTransform: 'none', borderRadius: 999, px: 2.5 }}
        >
          {String(t('sentences:help_menu.send'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactUsDialog;
